import React, { useState, useMemo } from "react";
import { charterSchools } from "./schoolDistrictsData";
import { motion, AnimatePresence } from "framer-motion";
import { Search, MapPin, ExternalLink, School, Info, Layers, CircleX } from "lucide-react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import "../styles/CharteredSchools.css";

import utahCounties from "../assets/utah-counties.json";

const BRAND = {
    darkBlue: "#203feb",
    red: "#D62828",
    light: "#f8fafc",
    dark: "#1e293b",
    blue: "#3b82f6"
};

const CharteredSchools = () => {
    const [selectedCounty, setSelectedCounty] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [tooltip, setTooltip] = useState("");
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });


    const totalSchools = useMemo(() => {
        return charterSchools.reduce((sum, c) => sum + (c["Number of Charter Schools"] || 0), 0);
    }, []);

    const maxSchools = useMemo(() => {
        return Math.max(...charterSchools.map((c) => c["Number of Charter Schools"] || 0));
    }, []);

    const colorScale = scaleLinear()
        .domain([0, maxSchools])
        .range(["#f1f5f9", BRAND.blue]);

    const filteredSchools = useMemo(() => {
        const query = searchQuery.toLowerCase();

        if (selectedCounty) {
            const county = charterSchools.find(c => c["County Name"] === selectedCounty);
            if (!county || !county["Charter Schools"]) return [];
            return county["Charter Schools"].filter(s =>
                s["Charter School Name"].toLowerCase().includes(query) ||
                s["Located at"].toLowerCase().includes(query)
            );
        } else if (query) {
            // Search across all counties
            let allMatches = [];
            charterSchools.forEach(county => {
                if (county["Charter Schools"]) {
                    const matches = county["Charter Schools"].filter(s =>
                        s["Charter School Name"].toLowerCase().includes(query) ||
                        s["Located at"].toLowerCase().includes(query)
                    );
                    allMatches = [...allMatches, ...matches];
                }
            });
            return allMatches;
        }
        return [];
    }, [selectedCounty, searchQuery]);

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-b from-blue-50 to-white">
            <LandingSection
                data={{
                    imageName: "utah-charter-school.webp",
                    title: "Chartered Schools",
                }}
            />

            <div className="charter-explorer-container">
                <div className="explorer-grid">
                    {/* LEFT COLUMN: MAP AREA */}
                    <div className="map-panel">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            className="card-glass map-container"
                        >
                            <div className="map-header">
                                <h3><Layers size={20} /> Interactive County Map</h3>
                                <p>Click a county to view charter school details</p>
                            </div>

                            <ComposableMap
                                projection="geoMercator"
                                projectionConfig={{ scale: 6000, center: [-111.9, 39.3] }}
                                className="rsm-map"
                            >
                                <Geographies geography={utahCounties}>
                                    {({ geographies }) =>
                                        geographies
                                            .filter((geo) => geo.id.startsWith("49")) // Utah FIPS
                                            .map((geo) => {
                                                const countyFips = geo.id;
                                                const fipsToCounty = {
                                                    "49001": "Beaver", "49003": "Box Elder", "49005": "Cache", "49007": "Carbon",
                                                    "49009": "Daggett", "49011": "Davis", "49013": "Duchesne", "49015": "Emery",
                                                    "49017": "Garfield", "49019": "Grand", "49021": "Iron", "49023": "Juab",
                                                    "49025": "Kane", "49027": "Millard", "49029": "Morgan", "49031": "Piute",
                                                    "49033": "Rich", "49035": "Salt Lake", "49037": "San Juan", "49039": "Sanpete",
                                                    "49041": "Sevier", "49043": "Summit", "49045": "Tooele", "49047": "Uintah",
                                                    "49049": "Utah", "49051": "Wasatch", "49053": "Washington", "49055": "Wayne",
                                                    "49057": "Weber"
                                                };
                                                const countyName = fipsToCounty[countyFips];
                                                const countyData = charterSchools.find(c => c["County Name"] === countyName);
                                                const schoolCount = countyData ? countyData["Number of Charter Schools"] : 0;

                                                return (
                                                    <Geography
                                                        key={geo.rsmKey}
                                                        geography={geo}
                                                        fill={selectedCounty === countyName ? BRAND.red : colorScale(schoolCount)}
                                                        stroke="#fff"
                                                        strokeWidth={1}
                                                        onMouseEnter={(e) => {
                                                            setTooltip(`${countyName}: ${schoolCount} Schools`);
                                                            setTooltipPos({ x: e.clientX, y: e.clientY });
                                                        }}
                                                        onMouseMove={(e) => {
                                                            setTooltipPos({ x: e.clientX, y: e.clientY });
                                                        }}
                                                        onMouseLeave={() => setTooltip("")}
                                                        onClick={() => setSelectedCounty(countyName)}
                                                        style={{
                                                            default: { outline: "none", transition: "all 0.3s" },
                                                            hover: { fill: BRAND.red, outline: "none", cursor: "pointer" },
                                                            pressed: { outline: "none" }
                                                        }}
                                                    />
                                                );
                                            })
                                    }
                                </Geographies>
                            </ComposableMap>

                            <div className="map-legend">
                                <span>0 Schools</span>
                                <div className="legend-gradient" />
                                <span>{maxSchools} Schools</span>
                            </div>
                        </motion.div>
                    </div>

                    {/* RIGHT COLUMN: DATA AREA */}
                    <div className="data-panel">
                        {/* STATS AREA */}
                        <div className="stats-row">
                            <motion.div initial={{ scale: 0.9, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} className="stat-card">
                                <School size={24} color={BRAND.darkBlue} />
                                <div>
                                    <span className="stat-val">{totalSchools}</span>
                                    <span className="stat-lab">Total Charter Schools</span>
                                </div>
                            </motion.div>
                            <motion.div initial={{ scale: 0.9, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} transition={{ delay: 0.1 }} className="stat-card">
                                <MapPin size={24} color={BRAND.red} />
                                <div>
                                    <span className="stat-val">{charterSchools.filter(c => c["Number of Charter Schools"] > 0).length}</span>
                                    <span className="stat-lab">Counties with Charters</span>
                                </div>
                            </motion.div>
                        </div>

                        {/* SEARCH AREA */}
                        <div className="search-box card-glass">
                            <Search className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search schools or locations..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {(searchQuery || selectedCounty) && (
                                <button
                                    className="clear-search-btn"
                                    onClick={() => {
                                        setSearchQuery("");
                                        setSelectedCounty(null);
                                    }}
                                    title="Clear all filters"
                                >
                                    <CircleX size={18} />
                                </button>
                            )}
                        </div>

                        {/* SELECTED COUNTY OR SEARCH RESULTS */}
                        <div className="results-container">
                            <AnimatePresence mode="wait">
                                {selectedCounty || searchQuery ? (
                                    <motion.div
                                        key={selectedCounty || "search-results"}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        className="county-results"
                                    >
                                        <div className="county-header">
                                            <h2>{selectedCounty ? `${selectedCounty} County` : "Search Results"}</h2>
                                            <div className="header-actions">
                                                <span className="count-badge">
                                                    {filteredSchools.length} Result{filteredSchools.length !== 1 ? 's' : ''}
                                                </span>
                                                <button
                                                    className="clear-all-small"
                                                    onClick={() => {
                                                        setSearchQuery("");
                                                        setSelectedCounty(null);
                                                    }}
                                                >
                                                    Clear All
                                                </button>
                                            </div>
                                        </div>

                                        {filteredSchools.length > 0 ? (
                                            <div className="school-list">
                                                {filteredSchools.map((school, i) => (
                                                    <motion.div
                                                        key={`${school["Charter School Name"]} -${i} `}
                                                        initial={{ opacity: 0, x: 20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: i * 0.05 }}
                                                        className="school-card card-glass"
                                                    >
                                                        <h4>{school["Charter School Name"]}</h4>
                                                        <div className="school-meta">
                                                            <span><MapPin size={14} /> {school["Located at"]}</span>
                                                            {school["Website"] && (
                                                                <a href={`https://${school["Website"].replace(/^https?:\/\//, "")}`} target="_blank" rel="noopener noreferrer">
                                                                    Website < ExternalLink size={14} />
                                                                </a >
                                                            )}
                                                        </div >
                                                    </motion.div >
                                                ))}
                                            </div >
                                        ) : (
                                            <div className="no-data">
                                                <Info size={40} />
                                                <p>No schools found matching your current search or selection.</p>
                                            </div>
                                        )}
                                    </motion.div >
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="welcome-box card-glass"
                                    >
                                        <School size={60} />
                                        <h2>Utah Charter Schools Guide</h2>
                                        <p>Select a county on the map to explore all local charter schools and their details.</p>
                                    </motion.div>
                                )}
                            </AnimatePresence >
                        </div >
                    </div >
                </div >
            </div >
            <div className="charter-explorer-container" style={{ paddingBottom: '2rem' }}>
                <div className="card-glass" style={{ padding: '2rem', marginTop: '2rem' }}>
                    <div className="flex items-center gap-2 mb-4">
                        <Info size={24} color={BRAND.darkBlue} />
                        <h3 className="text-xl font-bold" style={{ color: BRAND.dark }}>Data Sources</h3>
                    </div>
                    <div className="flex flex-col gap-3">
                        <p className="text-sm opacity-70 mb-2">For more detailed information, please visit the following resources:</p>
                        <div className="flex flex-wrap gap-4">
                            <a href="https://nces.ed.gov/ccd/districtsearch/" target="_blank" rel="noopener noreferrer" className="school-link flex items-center gap-1">
                                NCES District Search <ExternalLink size={14} />
                            </a>
                            <a href="https://schools.utah.gov/schoolsdirectory" target="_blank" rel="noopener noreferrer" className="school-link flex items-center gap-1">
                                Utah Schools Directory <ExternalLink size={14} />
                            </a>
                            <a href="https://www.niche.com/k12/search/best-schools/" target="_blank" rel="noopener noreferrer" className="school-link flex items-center gap-1">
                                Niche Best Schools <ExternalLink size={14} />
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tooltip */}
            <AnimatePresence>
                {tooltip && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        style={{
                            position: "fixed",
                            top: tooltipPos.y - 50,
                            left: tooltipPos.x + 20,
                            pointerEvents: "none",
                            zIndex: 9999,
                            background: "rgba(15, 23, 42, 0.9)",
                            backdropFilter: "blur(8px)",
                            color: "white",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            fontSize: "0.85rem",
                            fontWeight: "600",
                            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {tooltip}
                    </motion.div>
                )}
            </AnimatePresence>

            <Footer />
        </div >
    );
};

export default CharteredSchools;
